import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useContext,
  useRef,
} from 'react';
import {View, ScrollView} from 'react-native';
import {Divider} from 'react-native-paper';
import {useIsFocused, useNavigation} from '@react-navigation/native';
import {HeaderBackButton} from '@react-navigation/elements';
import moment from 'moment';
import _ from 'lodash';
import LogRocket from 'logrocket';

import sy from '~/styles';
import withProtectedScreen from '~/components/hoc/with-protected-screen';
import {
  Text,
  Pressable,
  ServiceRequestStatusIcon,
  Dialog,
  ReplacementIcon,
  WarrantyIcon,
  DealerPersona,
  LessorPersona,
  CarPersona,
  TabBar,
} from '~/components/controls';
import {Grouping} from '~/components/controls/3';
import {Row as GroupingRow} from '~/components/controls/3/Grouping';
import {Activity, Message} from '~/components/shared';
import {
  TaskActionsheet,
  TiresActionsheet,
} from '~/components/shared/Actionsheets';
import Format from '~/lib/format';
import {
  service_request as serviceRequestApi,
  messages as messagesApi,
} from '~/api';
import {
  STATUS_DESCRIPTIONS,
  STATUS_CODES,
  STATUS_ORDER,
} from '~/types/statuses';
import {ROB_TASK_STATUS, TIRE_REPAIR} from '~/types/rob';
import {SERVICE_TYPE_COMPONENTS_ORDER} from '~/types/services';
import {IsReplacementVehicle} from '~/types/request';
import {
  isRobTaskApproved,
  isRobTaskDenied,
  isRobTaskCancelled,
} from '~/types/rob';
import {SOURCES} from '~/types/sources';
import {Tabs} from '~/types/tabs';
import {useDispatch, useThrottledCallback} from '~/lib/hooks';
import {setAppDoneLoading, setAppIsLoading} from '~/actions';

import ChevronLeftIcon from '~/images/md-icons/chevron_left/materialicons/24px.svg';
import ThumbUpIcon from '~/images/md-icons/thumb_up/materialicons/24px.svg';
import ThumbDownIcon from '~/images/md-icons/thumb_down/materialicons/24px.svg';
import ListIcon from '~/images/md-icons/list/materialicons/24px.svg';
import EventIcon from '~/images/md-icons/event/materialicons/24px.svg';
import BusinessIcon from '~/images/md-icons/business/materialicons/24px.svg';
import AssignmentIcon from '~/images/md-icons/assignment/materialicons/24px.svg';
import ChatIcon from '~/images/md-icons/chat/materialicons/24px.svg';
import DiscountIcon from '~/images/md-icons/discount/materialsymbolsoutlined/24px.svg';
import PublishedWithChangesIcon from '~/images/md-icons/published_with_changes/materialicons/24px.svg';
import SellIcon from '~/images/md-icons/sell/materialsymbolsoutlined/24px.svg';
import MoreTimeIcon from '~/images/md-icons/more_time/materialicons/24px.svg';
import TimerIcon from '~/images/md-icons/timer/materialsymbolsoutlined/24px.svg';
import ProductionQuantityLimitsIcon from '~/images/md-icons/production_quantity_limits/materialsymbolsoutlined/24px.svg';
import AssignmentOutlinedIcon from '~/images/md-icons/assignment/materialsymbolsoutlined/24px.svg';
import ChatOutlinedIcon from '~/images/md-icons/chat/materialsymbolsoutlined/24px.svg';

const LessorRequestContext = React.createContext({});

const withLessorRequestContext = (Component) => (props) => {
  const {rob_request_id, rob_code, v} = props.route.params ?? {};

  const navigation = useNavigation();

  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [robContract, setRobContract] = useState(null);

  const dispatch = useDispatch();

  const refresh = useThrottledCallback(async () => {
    try {
      const serviceRequest = await serviceRequestApi.retrieve(rob_request_id);

      if (!serviceRequest) {
        dispatch(setAppDoneLoading());
        navigation.replace('Tabs', {
          screen: 'Search',
        });
        return false;
      }

      const {...overview} = serviceRequest;

      if (IsReplacementVehicle(overview.request_type)) {
        dispatch(setAppDoneLoading());
        navigation.replace('ReplacementVehicle', {
          rob_request_id,
        });
        return false;
      }

      setOverview(serviceRequest);

      setRobContract(serviceRequest.rob_contract);

      return true;
    } finally {
      setLoading(false);
    }
  }, [navigation, rob_request_id, dispatch]);

  useEffect(() => {
    if (overview) {
      LogRocket.track(
        'Lessor Request Details',
        _.pickBy({
          rob_request_id,
          license: overview?.license,
          status: overview?.status,
        }),
      );
    }
  }, [rob_request_id, overview]);

  useEffect(() => {
    if (loading) {
      dispatch(setAppIsLoading());
    } else {
      dispatch(setAppDoneLoading());
    }
  }, [loading, dispatch]);

  return (
    <LessorRequestContext.Provider
      value={{
        rob_request_id,
        rob_code,
        v,
        loading,
        overview,
        robContract,

        refresh,
        setRobContract,
      }}>
      <Component {...props} />
    </LessorRequestContext.Provider>
  );
};

const TaskSubtitle = ({
  lessor,
  tasks,
  rob_component,
  is_tires_item,
  is_sla_item,
  cancelled,
  cancellation_approved,
  cancellation_denied,
}) => {
  let content = <>&nbsp;</>;
  if (is_tires_item) {
    const tire_actions = tasks
      .flatMap((task) => task.parts ?? [])
      .flatMap((part) => {
        switch (part.action) {
          case 'Current_TireRepair':
            return part.repairs.map((item) =>
              item === TIRE_REPAIR.Wielomwisselen ? 'Omwisselen' : item,
            );
          case 'Current_Replace':
            return ['Bandvervanging'];
          case 'Current_Store':
          case 'Current_Remove':
            return ['Wissel zonder velg'];
          case 'Current_StoreWheel':
            return ['Wissel met velg'];

          default:
            break;
        }

        return [];
      });

    content = (
      <>SLA&nbsp;&middot;&nbsp;{[...new Set(tire_actions ?? [])].join(', ')}</>
    );
  } else if (is_sla_item) {
    content = <>SLA&nbsp;&middot;&nbsp;{lessor}</>;
  } else {
    let type;
    let operations;

    if (tasks.every((item) => !item.reference)) {
      type = 'Invoer';

      operations = [rob_component.operation.description];
    } else {
      const main_task = tasks.find((item) => item.is_main_task);
      type = main_task?.source ?? 'TecRMI';

      operations = tasks
        .sort((a, b) => {
          if (a.is_main_task) {
            return -1;
          }

          if (b.is_main_task) {
            return 1;
          }

          if (a.duration === b.duration) {
            return a.work_text.localeCompare(b.work_text);
          }

          return b.duration - a.duration;
        })
        .map((task) => task.work_text ?? 'Invoer');
    }

    content = (
      <>
        {type}
        {operations?.length > 0 ? (
          <>&nbsp;&middot;&nbsp;{operations.join(', ')}</>
        ) : (
          <></>
        )}
      </>
    );
  }

  return (
    <Text
      style={[
        sy.truncate,
        sy.small,
        sy['text-lightgray'],
        sy['underline'],
        (cancelled || cancellation_approved || cancellation_denied) &&
          sy['line-through'],
      ]}>
      {content}
    </Text>
  );
};

const Footer = ({amount}) => {
  return (
    <View
      style={[
        sy['bg-white'],
        sy['flex-row'],
        sy['justify-between'],
        sy['items-center'],
        sy['py-3'],
        sy['px-4'],
      ]}>
      <Text style={sy.mediumPlus}>{Format.price(amount)}</Text>
    </View>
  );
};

const MessagesTab = ({focused}) => {
  const [messages, setMessages] = useState([]);

  const {rob_request_id, overview} = useContext(LessorRequestContext);

  const fetch = useCallback(async () => {
    const messages = await messagesApi.retrieve(rob_request_id);

    setMessages(messages);
  }, [rob_request_id]);

  useEffect(() => {
    if (focused) {
      fetch();
    }
  }, [focused, fetch]);

  const scrollViewRef = useRef(null);

  const last_activity_id = Object.values(messages ?? {})
    .flatMap((item) => item)
    .filter((item) => item.activity_id)
    .slice(-1)?.[0]?.activity_id;

  return (
    <>
      <ScrollView
        style={[sy['flex-1'], sy['p-4'], sy['bg-gray95']]}
        contentContainerStyle={[sy['gap-4']]}
        ref={scrollViewRef}
        onContentSizeChange={() =>
          scrollViewRef.current?.scrollToEnd({animated: true})
        }>
        {messages &&
          Object.keys(messages).map((key) => (
            <View key={key} style={[sy['gap-2']]}>
              <Text
                style={[
                  sy.smallPlus,
                  {
                    textAlign: 'center',
                  },
                ]}>
                {moment(key, 'DD-MM-YYYY').format('D MMMM YYYY')}
              </Text>
              <View style={[sy['gap-4']]}>
                {messages[key].map((item) => {
                  const {
                    activity_id,
                    status,
                    timestamp,
                    attributes,
                    ...message
                  } = item;

                  // Create a new timestamp with date set to today, as we only need to display the time.
                  // Is it a hack? Maybe...
                  const display_timestamp = moment();
                  display_timestamp.set(
                    'hours',
                    moment(timestamp).get('hours'),
                  );
                  display_timestamp.set(
                    'minutes',
                    moment(timestamp).get('minutes'),
                  );

                  if (activity_id) {
                    return (
                      <Activity
                        key={activity_id}
                        attributes={attributes}
                        status={status}
                        display_timestamp={display_timestamp}
                        is_last_activity={activity_id === last_activity_id}
                      />
                    );
                  } else {
                    return (
                      <Message
                        key={message.message_id}
                        style={[sy['rounded-lg'], sy['p-2']]}
                        {...message}
                        company_name={overview?.dealer.company_name}
                        timestamp={display_timestamp}
                        rob_request_id={rob_request_id}
                      />
                    );
                  }
                })}
              </View>
            </View>
          ))}
      </ScrollView>
    </>
  );
};

const LessorRequestTab = ({focused}) => {
  const {overview, robContract, refresh, rob_code, v} =
    useContext(LessorRequestContext);

  const [showTaskEdit, setShowTaskEdit] = useState(null);
  const [showTiresEdit, setShowTiresEdit] = useState(null);
  const [showCustomTimeInfoDialog, setShowCustomTimeInfoDialog] =
    useState(false);
  const [showMoreTimeInfoDialog, setShowMoreTimeInfoDialog] = useState(false);
  const [showCustomArticlesInfoDialog, setShowCustomArticlesInfoDialog] =
    useState(false);
  const [showOverlapInfoDialog, setShowOverlapInfoDialog] = useState(false);
  const [showDiscountInfoDialog, setShowDiscountInfoDialog] = useState(false);
  const [showMarkupInfoDialog, setShowMarkupInfoDialog] = useState(false);

  useEffect(() => {
    if (focused) {
      refresh();
    }
  }, [focused, refresh]);

  useEffect(() => {
    console.log('Overview or rob_code changed', v);

    if ((overview, rob_code)) {
      const component = overview.components.find((c) => {
        const task = c.tasks?.find((t) => t.is_main_task);
        return task?.rob_code === rob_code;
      });

      if (component) {
        const task = component.tasks.find((t) => t.is_main_task);

        setShowTaskEdit({
          tasks: component.tasks,
          parts: [...(task.parts ?? []), ...(component.parts ?? [])],
          component,
        });
      }
    }
  }, [overview, rob_code, v]);

  const components =
    overview?.components.sort((component_a, component_b) => {
      const task_a = component_a.tasks?.find((t) => t.is_main_task);
      const task_b = component_b.tasks?.find((t) => t.is_main_task);

      if (!task_a) {
        return 1;
      }

      if (!task_b) {
        return -1;
      }

      if (task_a.rob_code === '1039') {
        return 1;
      }

      if (task_b.rob_code === '1039') {
        return -1;
      }

      const component_a_index = SERVICE_TYPE_COMPONENTS_ORDER.indexOf(
        component_a.name,
      );
      const component_b_index = SERVICE_TYPE_COMPONENTS_ORDER.indexOf(
        component_b.name,
      );
      if (component_a_index >= 0 || component_b_index >= 0) {
        if (component_a_index === -1) {
          return 1;
        }

        if (component_b_index === -1) {
          return -1;
        }

        if (component_a_index !== component_b_index) {
          return component_a_index - component_b_index;
        }
      }

      return task_a.rob_code.localeCompare(task_b.rob_code);
    }) ?? [];

  let total_amount = overview?.components.reduce(
    (acc, component) =>
      isRobTaskCancelled(component.rob_component?.status.code)
        ? acc
        : acc + (component.rob_component?.price ?? 0),
    0,
  );

  if (!overview) {
    return null;
  }

  return (
    <>
      <ScrollView
        style={[sy['bg-gray95']]}
        contentContainerStyle={[sy['p-2'], sy['gap-2']]}>
        <Grouping
          icon={
            <DealerPersona
              dealer_id={overview?.dealer.id}
              company_name={overview?.dealer.company_name}
              fallback={<EventIcon fill="#4A4A49" />}
              size={24}
            />
          }
          header={
            <>
              <Text style={[sy.mediumPlus, sy.capitalize]}>
                {moment(overview?.datetime, 'YYYY-MM-DD').format('dddd')}
              </Text>
              &nbsp;
              {moment(overview?.datetime, 'YYYY-MM-DD').format('D MMMM')}
              &nbsp;&middot;&nbsp;
              {moment(overview?.datetime).format('H:mm')}
              &nbsp;uur
            </>
          }
          options={{topMost: true}}>
          <GroupingRow title="Formule">
            <Text>{overview?.dealer.company_name}</Text>
          </GroupingRow>
          <GroupingRow title="Vestiging" contentStyle={[sy['flex-1']]}>
            <Text style={[sy['truncate'], sy['text-right']]}>
              {overview?.dealer.city}&nbsp;&middot;&nbsp;
              {overview?.dealer.short_name}
            </Text>
          </GroupingRow>
          <GroupingRow title="Datum">
            <Text>
              {moment(overview?.datetime, 'YYYY-MM-DD').format('D MMMM YYYY')}
            </Text>
          </GroupingRow>
          <GroupingRow title="Tijd">
            <Text>{moment(overview?.datetime).format('H:mm')}</Text>
          </GroupingRow>
          {STATUS_ORDER.indexOf(overview.status) <
          STATUS_ORDER.indexOf(STATUS_CODES.Done) ? (
            <GroupingRow title="Gereed (verwacht)">
              <Text>
                {moment(
                  overview?.expected_completion_date,
                  'YYYY-MM-DD',
                ).format('D MMMM YYYY')}
              </Text>
            </GroupingRow>
          ) : (
            <GroupingRow title="Gereed">
              <Text>
                {moment(overview?.completion_date, 'YYYY-MM-DD').format(
                  'D MMMM YYYY',
                )}
              </Text>
            </GroupingRow>
          )}
          <GroupingRow title="Opdrachtnummer">
            <Text>{overview?.service_request_number ?? '-'}</Text>
          </GroupingRow>
        </Grouping>

        <Grouping
          icon={<CarPersona size={24} brand={overview?.car.brand} />}
          header={overview?.car.name}>
          <GroupingRow
            title={
              <View style={[sy['flex-row'], sy['items-center']]}>
                <Text>Chassisnr. (VIN)</Text>
              </View>
            }>
            <Text>
              {overview?.vin_number?.length ? (
                overview.vin_number
              ) : (
                <>Niet opgegeven</>
              )}
            </Text>
          </GroupingRow>
          <GroupingRow title="Brandstof">
            <Text>{overview?.car.fuel_type}</Text>
          </GroupingRow>
          <GroupingRow title="APK vervaldatum">
            <Text>{moment(overview?.car?.apk_expiry).format('D-M-YYYY')}</Text>
          </GroupingRow>
          <GroupingRow title="Kilometerstand">
            <Text>
              {Format.number(overview?.kilometrage, 0, true) || (
                <>Niet opgegeven</>
              )}
            </Text>
          </GroupingRow>
          <GroupingRow title="Eerste toelating">
            <Text>
              {moment(overview?.car?.entry_date).format('D-M-YYYY')}&nbsp;(
              {Format.number(
                moment().diff(moment(overview?.car?.entry_date), 'months') / 12,
                1,
                false,
                {minimumFractionDigits: 0},
              )}
              &nbsp;jaar)
            </Text>
          </GroupingRow>
          <GroupingRow title="Verzwaard gebruik">
            <Text>
              {overview?.car?.attributes?.heavy_use ? <>Ja</> : <>Nee</>}
            </Text>
          </GroupingRow>
        </Grouping>

        <Grouping
          icon={
            <LessorPersona
              lessor={overview?.lessor}
              fallback={<BusinessIcon fill={'#4A4A49'} />}
              size={24}
            />
          }
          header={overview?.lessor}>
          {robContract?.leaseContract?.endDate && (
            <GroupingRow
              title={
                <View style={[sy['flex-row'], sy['items-center']]}>
                  <Text>Einde contract</Text>
                </View>
              }>
              <Text>
                {moment(robContract?.leaseContract.endDate).format('D-M-YYYY')}
                &nbsp;(
                {Format.number(
                  robContract?.leaseContract.maximumMileage,
                  0,
                  true,
                )}{' '}
                km)
              </Text>
            </GroupingRow>
          )}
          <GroupingRow title="Reparatie & onderhoud">
            <Text>
              {robContract?.permissions.repairAndMaintenance ? (
                <>Toegestaan</>
              ) : (
                <>Niet toegestaan</>
              )}
            </Text>
          </GroupingRow>
          <GroupingRow title="Schade">
            <Text>Toegestaan</Text>
          </GroupingRow>
          <GroupingRow title="Bandvervanging">
            <Text>
              {robContract?.permissions.tires.replace ? (
                <>Toegestaan</>
              ) : (
                <>Niet toegestaan</>
              )}
            </Text>
          </GroupingRow>
          <GroupingRow title="Seizoenswissel">
            <Text>
              {robContract?.permissions.tires.swap ? (
                <>Toegestaan</>
              ) : (
                <>Niet toegestaan</>
              )}
            </Text>
          </GroupingRow>
          <GroupingRow title="Vervangend vervoer">
            <Text>
              {robContract?.permissions.replacement_vehicle?.allowed ? (
                <>Toegestaan</>
              ) : (
                <>Niet toegestaan</>
              )}
            </Text>
          </GroupingRow>
        </Grouping>
        <View style={[sy['bg-white'], sy['rounded-lg'], sy['overflow-hidden']]}>
          <View style={[sy['flex-row'], sy['justify-between'], sy['p-4']]}>
            <View style={[sy['flex-row'], sy['flex-1'], sy['gap-4']]}>
              <AssignmentIcon fill="#4A4A49" />
              <View>
                <Text style={sy.mediumPlus}>
                  Opdracht&nbsp;&middot;&nbsp;
                  {STATUS_DESCRIPTIONS[overview.status]}
                </Text>
              </View>
            </View>
            {overview?.status !== STATUS_CODES.CheckIn && (
              <Text style={sy.mediumPlus}>{Format.price(total_amount)}</Text>
            )}
          </View>
          <Divider style={sy['bg-gray88']} />
          {overview?.status === STATUS_CODES.CheckIn && (
            <View
              style={[
                sy['p-4'],
                sy['bg-gray95'],
                sy['items-center'],
                sy['justify-center'],
              ]}>
              <Text style={[sy.smallMedium, sy.italic]}>
                Inhoud volgt bij Voorstel
              </Text>
            </View>
          )}

          {components?.map((component) => {
            const task = component.tasks?.find((t) => t.is_main_task);
            if (!task) {
              return null;
            }

            const is_sla_item = _.isNumber(task.amount);

            const cancelled =
              component.rob_component.status.code === ROB_TASK_STATUS.Cancelled;

            const cancellation_approved =
              component.rob_component.status.code ===
              ROB_TASK_STATUS.CancellationApproved;

            const cancellation_denied =
              component.rob_component.status.code ===
              ROB_TASK_STATUS.CancellationDenied;

            const has_custom_articles = task.parts?.some(
              (item) => item.source === SOURCES.Custom,
            );

            const has_discount =
              task.discount > 0 ||
              task.parts?.some((item) => item.discount > 0);
            const has_markup =
              task.markup > 0 || task.parts?.some((item) => item.markup > 0);

            const has_custom_time = _.isNil(task.original_work_time);

            const has_more_time =
              !has_custom_time &&
              component.tasks.some(
                (x) =>
                  x.duration >
                  (_.isFinite(x.calculated_work_time)
                    ? x.calculated_work_time
                    : x.original_work_time),
              );

            const has_work_time_overlap = component.tasks.some(
              (x) =>
                _.isNil(x.original_duration) &&
                _.isFinite(x.calculated_work_time),
            );

            const onTaskPress = () => {
              if (cancelled || cancellation_approved || cancellation_denied) {
                return;
              }

              if (component.name === 'Banden') {
                setShowTiresEdit({task, component});
              } else {
                setShowTaskEdit({
                  tasks: component.tasks,
                  parts: [...(task.parts ?? []), ...(component.parts ?? [])],
                  component,
                });
              }
            };

            return (
              <React.Fragment key={task.id}>
                {
                  <>
                    <View
                      style={[
                        sy['flex-row'],
                        sy['flex-1'],
                        sy['p-4'],
                        sy['gap-4'],
                      ]}>
                      <Pressable
                        style={[sy['justify-start'], {width: 'auto'}]}
                        disabled={
                          cancelled ||
                          cancellation_approved ||
                          cancellation_denied
                        }
                        onPress={onTaskPress}>
                        {isRobTaskDenied(
                          component.rob_component?.status.code,
                        ) ? (
                          <ThumbDownIcon fill="#972727" />
                        ) : isRobTaskApproved(
                            component.rob_component.status.code,
                          ) ? (
                          <ThumbUpIcon fill="#299976" />
                        ) : task.rob_code === '1039' ? (
                          <ReplacementIcon type="transport" />
                        ) : (
                          <ListIcon />
                        )}
                      </Pressable>
                      <View style={[sy['flex-1']]}>
                        <Pressable
                          disabled={
                            cancelled ||
                            cancellation_approved ||
                            cancellation_denied
                          }
                          onPress={onTaskPress}>
                          <View style={[sy['flex-row'], sy['justify-between']]}>
                            <View style={[sy['flex-1']]}>
                              <View style={[sy['flex-row']]}>
                                <Text
                                  style={[
                                    (cancelled ||
                                      cancellation_approved ||
                                      cancellation_denied) &&
                                      sy['line-through'],
                                    sy.truncate,
                                  ]}>
                                  {component.rob_component.rob.code}
                                  &nbsp;&middot;&nbsp;
                                  {component.rob_component.rob.description}
                                </Text>
                              </View>
                              <TaskSubtitle
                                lessor={overview?.lessor}
                                tasks={component.tasks}
                                rob_component={component.rob_component}
                                is_maintenance_item={
                                  component.name === 'Onderhoud'
                                }
                                is_tires_item={component.name === 'Banden'}
                                is_sla_item={is_sla_item}
                                cancelled={cancelled}
                                cancellation_approved={cancellation_approved}
                                cancellation_denied={cancellation_denied}
                              />
                            </View>
                          </View>
                        </Pressable>
                      </View>
                      <View style={[sy['items-end']]}>
                        <Text style={sy.smallRegular}>
                          {Format.price(
                            cancelled ||
                              cancellation_approved ||
                              cancellation_denied
                              ? 0
                              : component.rob_component.price ?? 0,
                          )}
                        </Text>
                        <View
                          style={[
                            sy['flex-row'],
                            sy['items-center'],
                            sy['gap-1'],
                          ]}>
                          {(task.warranty || task.is_damage) && (
                            <WarrantyIcon
                              description={
                                component.rob_component.rob.description
                              }
                              value={{
                                warranty: task.warranty,
                                is_damage: task.is_damage,
                                leniency_amount: task.leniency_amount,
                              }}
                              width={18}
                              height={18}
                            />
                          )}
                          {!is_sla_item &&
                            (has_custom_time ? (
                              <Pressable
                                onPress={() =>
                                  setShowCustomTimeInfoDialog(true)
                                }>
                                <TimerIcon
                                  fill="#FF6C00"
                                  width={18}
                                  height={18}
                                />
                              </Pressable>
                            ) : has_more_time ? (
                              <Pressable
                                onPress={() => setShowMoreTimeInfoDialog(true)}>
                                <MoreTimeIcon
                                  fill="#FF6C00"
                                  width={18}
                                  height={18}
                                />
                              </Pressable>
                            ) : null)}
                          {!has_more_time && has_work_time_overlap && (
                            <Pressable
                              onPress={() => setShowOverlapInfoDialog(true)}>
                              <PublishedWithChangesIcon
                                fill="#299976"
                                width={18}
                                height={18}
                              />
                            </Pressable>
                          )}
                          {has_custom_articles && (
                            <Pressable
                              onPress={() =>
                                setShowCustomArticlesInfoDialog(true)
                              }>
                              <ProductionQuantityLimitsIcon
                                fill="#FF6C00"
                                width={18}
                                height={18}
                              />
                            </Pressable>
                          )}
                          {has_discount && (
                            <Pressable
                              onPress={() => setShowDiscountInfoDialog(true)}>
                              <DiscountIcon
                                fill="#299976"
                                width={18}
                                height={18}
                              />
                            </Pressable>
                          )}
                          {has_markup && (
                            <Pressable
                              onPress={() => setShowMarkupInfoDialog(true)}>
                              <SellIcon fill="#FF6C00" width={18} height={18} />
                            </Pressable>
                          )}
                        </View>
                      </View>
                    </View>
                    <View style={[sy['px-4']]}>
                      <Divider style={[sy['bg-gray95']]} />
                    </View>
                  </>
                }
              </React.Fragment>
            );
          })}
        </View>
      </ScrollView>

      <Divider style={sy['bg-gray88']} />
      <Footer amount={total_amount} />

      {showTaskEdit && (
        <TaskActionsheet
          onDismiss={() => setShowTaskEdit(null)}
          is_denied={isRobTaskDenied(
            showTaskEdit.component.rob_component?.status.code,
          )}
          is_cancelled={isRobTaskCancelled(
            showTaskEdit.component.rob_component?.status.code,
          )}
          lessor={overview.lessor}
          service_request_id={overview.service_request_id}
          tasks={showTaskEdit.tasks}
          parts={showTaskEdit.parts}
          component={showTaskEdit.component}
          is_replacement={
            showTaskEdit.component.rob_component?.rob.code === '1039'
          }
        />
      )}

      {showTiresEdit && (
        <TiresActionsheet
          onDismiss={() => setShowTiresEdit(null)}
          sla={overview.sla?.tires}
          lessor={overview.lessor}
          task={showTiresEdit.task}
          component={showTiresEdit.component}
        />
      )}

      <Dialog
        visible={showCustomTimeInfoDialog}
        title="Arbeidstijd ingevoerd"
        titleIcon={<TimerIcon fill="#FF6C00" />}
        onDismiss={() => setShowCustomTimeInfoDialog(false)}>
        <View style={[sy['py-4']]}>
          <Text>
            De huidige waarde van de arbeidstijd is handmatig ingevoerd.
          </Text>
        </View>
      </Dialog>

      <Dialog
        visible={showMoreTimeInfoDialog}
        title="Arbeidstijd bijgewerkt"
        titleIcon={<MoreTimeIcon fill="#FF6C00" />}
        onDismiss={() => setShowMoreTimeInfoDialog(false)}>
        <View style={[sy['py-4']]}>
          <Text>
            De arbeidstijd is handmatig aangepast naar een andere waarde.
          </Text>
        </View>
      </Dialog>

      <Dialog
        visible={showCustomArticlesInfoDialog}
        title="Artikel ingevoerd"
        titleIcon={<ProductionQuantityLimitsIcon fill="#FF6C00" />}
        onDismiss={() => setShowCustomArticlesInfoDialog(false)}>
        <View style={[sy['py-4']]}>
          <Text>
            Handmatig ingevoerd(e) artikel(en) aanwezig in activiteit.
          </Text>
        </View>
      </Dialog>

      <Dialog
        visible={showOverlapInfoDialog}
        title="Overlapcorrectie"
        titleIcon={<PublishedWithChangesIcon fill="#299976" />}
        onDismiss={() => setShowOverlapInfoDialog(false)}>
        <View style={[sy['py-4']]}>
          <Text>
            Arbeidstijd is bijgewerkt door overlap met andere werkzaamheden in
            werkopdracht.
          </Text>
        </View>
      </Dialog>

      <Dialog
        visible={showDiscountInfoDialog}
        title="Korting toegepast"
        titleIcon={<DiscountIcon fill="#299976" />}
        onDismiss={() => setShowDiscountInfoDialog(false)}>
        <View style={[sy['py-4']]}>
          <Text>Lager bedrag door toepassing korting.</Text>
        </View>
      </Dialog>

      <Dialog
        visible={showMarkupInfoDialog}
        title="Toeslag toegepast"
        titleIcon={<SellIcon fill="#FF6C00" />}
        onDismiss={() => setShowMarkupInfoDialog(false)}>
        <View style={[sy['py-4']]}>
          <Text>Hoger bedrag door toepassing toeslag.</Text>
        </View>
      </Dialog>
    </>
  );
};

const Index = ({navigation, route}) => {
  const initialTab = route.params?.tab ?? Tabs.Request;

  const {rob_request_id, loading, overview, refresh} =
    useContext(LessorRequestContext);

  const [selectedTab, setSelectedTab] = useState(initialTab);

  const isFocused = useIsFocused();

  useLayoutEffect(() => {
    navigation.setOptions({
      headerShown: true,
      headerLeft: () => (
        <HeaderBackButton
          backImage={() => <ChevronLeftIcon />}
          onPress={() => {
            if (navigation.canGoBack()) {
              navigation.goBack();
            } else {
              navigation.replace('Tabs', {
                screen: 'Search',
              });
            }
          }}
        />
      ),
      headerStatusBarHeight: 0,
      headerTitleAlign: 'center',
      headerStyle: {
        borderBottomColor: 'transparent',
        boxShadow: 'none',
      },
      headerTitle: () =>
        loading ? null : (
          <View style={[sy['items-center'], sy['gap-1']]}>
            <Text style={sy.largePlus}>
              {Format.license(overview?.license)}
            </Text>
          </View>
        ),
      headerRight: () =>
        loading ? null : (
          <ServiceRequestStatusIcon
            style={{marginRight: 16}}
            status={overview.status}
          />
        ),
    });
  }, [navigation, overview, loading]);

  useEffect(() => {
    if (isFocused) {
      refresh();
    }
  }, [isFocused, rob_request_id, refresh]);

  return (
    <View style={sy.mainView}>
      {!loading && (
        <>
          <TabBar tab={selectedTab} onChangeTab={setSelectedTab}>
            <TabBar.Tab
              label={Tabs.Request}
              icon={{
                filled: <AssignmentIcon />,
                outlined: <AssignmentOutlinedIcon />,
              }}
            />
            <TabBar.Tab
              label={Tabs.Messages}
              icon={{
                filled: <ChatIcon />,
                outlined: <ChatOutlinedIcon />,
              }}
            />
          </TabBar>
          <Divider style={sy['bg-gray88']} />
          <TabBar.Content tab={selectedTab}>
            <LessorRequestTab tab={Tabs.Request} />
            <MessagesTab tab={Tabs.Messages} />
          </TabBar.Content>
        </>
      )}
    </View>
  );
};

export default withProtectedScreen(withLessorRequestContext(Index));
